import useSWR from 'swr';

import {
  distributionFromEnvelope,
  MetricDistribution,
  MetricEnvelope,
  MetricResultRow,
  rowsFromEnvelope,
  toMetricResult,
  wtrReasonsOf
} from 'containers/Intelligence/data/metricResult';
import {
  isPopupIdentifier,
  PopupTable,
  popupTableFromEnvelope
} from 'containers/Intelligence/data/popupResult';
import {
  isProductReadinessEnvelope,
  productReadinessRowsFromEnvelope,
  ProductReadinessRow
} from 'containers/Intelligence/data/productReadinessResult';
import { reversalFromEnvelope, ReversalResult } from 'containers/Intelligence/data/reversalResult';
import { WtrReason } from 'containers/Intelligence/data/wtr';

import { fetcher } from './utils';

// The Customer Insights metric endpoint. ONE hop: perx-api proxies Metabase, resolving the tenant,
// timezone and ACL server-side into a signed JWT, so the dashboard never mints a token and never
// holds a question id. Contract PLA-8817; plan `metabase-data-seam.md` § V.1. PLA-8815.
export const METRICS_PATH = '/v4/dash/customer_intelligence/metrics';

// `metric_id` is the catalogue Metric ID verbatim (`O-COM-013`), from `registry/metricIdentifiers`.
// A numeric Metabase question id here would be a bug: this Metabase addresses cards by number and
// the ids differ per deployment, which is exactly why the resolution is server-side.
export const metricPath = (identifier: string): string =>
  `${METRICS_PATH}/${encodeURIComponent(identifier)}`;

/**
 * One metric's request.
 *
 * `query` is forwarded to Metabase VERBATIM — perx-api does not enumerate, validate, default or
 * reshape it. Two consequences shape everything below.
 *
 * FIRST, there is no 400 on this endpoint. A misspelled key is not rejected; it reaches Metabase and
 * comes back 502, which is indistinguishable from a real outage. So the parameter vocabulary has to
 * be owned in one place on this side, because the backend will never tell us we got it wrong.
 *
 * SECOND, the scope parameters are absent by design, not by omission. `tenant`, `timezone` and
 * `acl_global_ids` are stripped and re-derived server-side, and a client-sent copy is discarded — so
 * this type deliberately has no slot for them. A field that must always be empty is a trap, not a
 * safeguard.
 *
 * Adding a filter is an entry in `query` and nothing else: no new type, no hook signature, no
 * cache-key function. That is the point of the shape.
 */
export interface MetricRequest {
  identifier: string;
  /** Flat keys only — see `EMPTY_QUERY`. */
  query?: Record<string, string>;
}

/**
 * The default, and for now the only safe one.
 *
 * Two independent reasons to send nothing yet, neither of which is timidity:
 *
 * 1. The Layer 2 card SQL declares only `{{tenant_id}}` and `metric_id`, so no Layer 2 card CONSUMES
 *    a date or filter parameter today. Whether Metabase's embed endpoint rejects a parameter the
 *    card does not declare is unverified — if it does, every filtered request 502s.
 * 2. Nested encoding (`filters[lifecycle]=new`) rides Faraday's default nested encoder and the
 *    contract says to confirm the wire format against a real nested filter before relying on it.
 *
 * The no-parameter path is the one dev-tested 121/121 on UAT. Turning filters on is a matter of
 * passing a populated `query`, once the card SQL declares the parameters and one card has been
 * tested — NOT a matter of changing this module.
 */
export const EMPTY_QUERY: Record<string, string> = {};

// perx-api's failure modes, kept apart because they mean genuinely different things to a reader.
// Contract PLA-8817 § errors.
export type MetricFetchOutcome =
  | {
      kind: 'rows';
      rows: MetricResultRow[];
      /**
       * Present only when the response is a DISTRIBUTION — one row per lifecycle stage or holding
       * band, no `metric_value` column. Carried ALONGSIDE `rows` rather than instead of it: the
       * scalar parse still resolves the period window off the same rows, which is what dates the
       * card, and a consumer that knows nothing about distributions is unaffected by an extra
       * optional field. PLA-8815.
       */
      distribution?: MetricDistribution;
      /**
       * The `*-PR-*` schema's own rows, present ONLY when the envelope's `cols` declare it. Additive
       * and separate because that schema has no `metric_value` and no `calculation_status`: `rows`
       * above still parses (into a row of nulls with an empty status), and every existing consumer
       * keeps reading exactly what it always read. PLA-8825, PLA-8828.
       */
      readiness?: ProductReadinessRow[];
      /** Likewise for `C-REV-002`/`003`/`004`. `C-REV-001` is a standard row and is absent here. */
      reversal?: ReversalResult;
      /**
       * The response's `result_role: wtr` rows, ranked. `rows` above carries each one on its own
       * `wtr` field; this is the metric-level collection of them, so a consumer holding an outcome
       * does not have to re-derive the ranking `wtrReasonsOf` already applied. Absent when the metric
       * named no reason, which is every metric checked on UAT so far. PLA-8815, PLA-8828.
       */
      reasons?: WtrReason[];
    }
  // 403. FIVE causes collapse into one identical body — metric absent from the catalogue,
  // `intelligence_suite` off, metric outside the tenant's industry, a missing permission-chain link,
  // or an unrecognised Host. They are deliberately indistinguishable, so nothing downstream may
  // guess which: one generic "not available" is the only honest rendering.
  | { kind: 'forbidden' }
  // 404 `40415`. A DESIGNED empty-state signal, not a fault: the question id has not landed for this
  // deployment yet. Both production id files are empty today, so in production this is currently the
  // answer for every metric.
  | { kind: 'notDeployed' }
  // 502 `50201` (Metabase non-2xx, non-JSON, unset signing secret, or a body whose `status` is not
  // `completed`), 504 `50401` (timeout), and anything unrecognised.
  | { kind: 'technicalError' };

export const NOT_DEPLOYED_CODE = 40415;

const outcomeFromError = (error: any): MetricFetchOutcome => {
  const status = error?.response?.status;

  if (status === 403) {
    return { kind: 'forbidden' };
  }

  // The code is checked rather than the status alone: a 404 from a mistyped PATH is a fault, while
  // `40415` is the backend telling us this metric is not deployed here. Reading every 404 as the
  // designed signal would render "not configured" for a broken route.
  if (status === 404 && error?.response?.data?.code === NOT_DEPLOYED_CODE) {
    return { kind: 'notDeployed' };
  }

  return { kind: 'technicalError' };
};

const outcomeFromEnvelope = (envelope: MetricEnvelope): MetricFetchOutcome => {
  const distribution = distributionFromEnvelope(envelope);
  const reversal = reversalFromEnvelope(envelope);
  const rows = rowsFromEnvelope(envelope);
  const reasons = wtrReasonsOf(rows.map(toMetricResult));

  return {
    kind: 'rows',
    rows,
    // Spread, so an outcome for a metric on the standard schema carries none of the four keys --
    // the shape 27 shipped Layer 1 cards already assert on.
    ...(distribution ? { distribution } : {}),
    ...(isProductReadinessEnvelope(envelope)
      ? { readiness: productReadinessRowsFromEnvelope(envelope) }
      : {}),
    ...(reversal ? { reversal } : {}),
    ...(reasons.length > 0 ? { reasons } : {})
  };
};

const requestMetric = async (
  identifier: string,
  params: Record<string, string>
): Promise<MetricFetchOutcome> => {
  try {
    const body = await fetcher({ url: metricPath(identifier), params } as any);

    return outcomeFromEnvelope(body?.data);
  } catch (error) {
    return outcomeFromError(error);
  }
};

/**
 * Asks every metric for its "Why This Result" rows. `with_wtr=true` returns the same main rows plus
 * `result_role: 'wtr'` rows (Data team Phase 1B handoff); the parsers above keep those out of every
 * reading, and `wtrReasonsOf` collects them.
 */
export const WITH_WTR_KEY = 'with_wtr';

export const fetchMetric = async (request: MetricRequest): Promise<MetricFetchOutcome> => {
  const query = request.query ?? EMPTY_QUERY;

  // `with_wtr` is inert on popups, so they are asked exactly what the caller asked.
  if (isPopupIdentifier(request.identifier)) {
    return requestMetric(request.identifier, query);
  }

  const withReasons = await requestMetric(request.identifier, { ...query, [WITH_WTR_KEY]: 'true' });

  // Measured 2026-09-14 (`data/wtr.ts`): eight metrics whose WTR keys were not yet implemented
  // answered 502 WITH the parameter and 200 without it. One retry without it keeps their reading.
  return withReasons.kind === 'technicalError'
    ? requestMetric(request.identifier, query)
    : withReasons;
};

export interface UseMetricResult {
  outcome: MetricFetchOutcome | undefined;
  isLoading: boolean;
  mutate: () => void;
}

/**
 * One metric, one call — the contract's own model: perx-api knows nothing about which metrics
 * compose which card, so composition stays on this side.
 *
 * The SWR key is the request itself, so it widens on its own the day `query` carries a filter. There
 * is no hand-assembled cache key to keep in step, which is the failure the fixture seam's
 * `cacheKeyFor` was heading towards once the contract went per-metric.
 *
 * A `null` request disables the fetch entirely (SWR's null-key convention), which is how a card the
 * role cannot view is never requested rather than requested and then discarded.
 */
export const useMetricResult = (request: MetricRequest | null): UseMetricResult => {
  const { data, isLoading, mutate } = useSWR<MetricFetchOutcome>(
    request ? { scope: METRICS_PATH, identifier: request.identifier, query: request.query } : null,
    // Failures are resolved into an outcome rather than thrown, because every one of them is a
    // state this screen renders. SWR's `error` would collapse "not deployed here" and "Metabase is
    // down" into the same undefined data, and those owe the reader different words.
    () => fetchMetric(request as MetricRequest)
  );

  return { outcome: data, isLoading, mutate };
};

// A popup's own answer. Same failure vocabulary as a metric's; a popup has no reading, so what it
// carries on success is its table.
export type PopupFetchOutcome =
  | { kind: 'rows'; table: PopupTable }
  | Exclude<MetricFetchOutcome, { kind: 'rows' }>;

export const fetchPopup = async (request: MetricRequest): Promise<PopupFetchOutcome> => {
  try {
    const body = await fetcher({
      url: metricPath(request.identifier),
      params: request.query ?? EMPTY_QUERY
    } as any);

    return { kind: 'rows', table: popupTableFromEnvelope(body?.data) };
  } catch (error) {
    return outcomeFromError(error) as PopupFetchOutcome;
  }
};

export interface UsePopupResult {
  outcome: PopupFetchOutcome | undefined;
  isLoading: boolean;
  mutate: () => void;
}

/**
 * One popup, fetched when the overlay that renders it mounts -- which is when the reader opens it,
 * never on page load. A `null` request fetches nothing.
 */
export const usePopupResult = (request: MetricRequest | null): UsePopupResult => {
  const { data, isLoading, mutate } = useSWR<PopupFetchOutcome>(
    request
      ? { scope: `${METRICS_PATH}#popup`, identifier: request.identifier, query: request.query }
      : null,
    () => fetchPopup(request as MetricRequest),
    // A block that read its popup on render hands the same answer to its View More overlay.
    { revalidateIfStale: false }
  );

  return { outcome: data, isLoading, mutate };
};

export default useMetricResult;
